import React, { useEffect, useMemo, useRef, useState } from "react";
import { T } from "../../app/tokens.js";
import {
  createProjectBudget,
  deleteProjectBudget,
  updateProjectBudget,
} from "../../data/projectsClient.js";
import { log } from "../../utils/logger.js";
import { getCurrencySymbol } from "../../utils/format.js";

export const BUDGET_CATEGORY_LABELS = {
  soft: "Soft Costs",
  hard: "Hard Costs",
  ffe: "FF&E",
  contingency: "Contingency",
  tia: "TIA",
  other: "Other",
};

const CATEGORY_ORDER = ["soft", "hard", "ffe", "contingency", "other", "tia"];
const COST_CATEGORIES = new Set(["soft", "hard", "ffe"]);
const inputStyle = {
  width: "100%",
  boxSizing: "border-box",
  border: `1px solid ${T.border}`,
  borderRadius: 5,
  padding: "5px 6px",
  fontSize: T.fsXs,
  color: T.dark,
  background: T.white,
};

function numberOrNull(value) {
  if (value === "" || value === null || value === undefined) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

export function formatBudgetCell(value, currency) {
  if (value === null || value === undefined || value === "") return "—";
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  const sign = n < 0 ? "−" : "";
  const ccy = currency || "";
  const symbol = getCurrencySymbol(ccy);
  const prefix = symbol !== ccy ? symbol : "";
  const code = ccy ? ` ${ccy}` : "";
  return `${sign}${prefix}${Math.abs(n).toLocaleString(undefined, { maximumFractionDigits: 2 })}${code}`;
}

function sumNullable(rows, key) {
  const values = rows.map((row) => row[key]).filter((value) => value !== null && value !== undefined && value !== "");
  return values.length ? values.reduce((total, value) => total + Number(value), 0) : null;
}

function normalizedCurrency(value) {
  return value == null ? "" : String(value).trim().toUpperCase();
}

function hasPopulatedTotal(row) {
  return ["budget", "committed", "forecast", "invoiced"].some((key) => row[key] != null && row[key] !== "");
}

export function deriveBudgetTotals(budgets) {
  const rows = Array.isArray(budgets) ? budgets : [];
  const costRows = rows.filter((row) => COST_CATEGORIES.has(row.category));
  const totalRows = rows.filter((row) => ["soft", "hard", "ffe", "contingency", "other"].includes(row.category));
  const currencies = [...new Set(totalRows.map((row) => normalizedCurrency(row.currency)).filter(Boolean))];
  const mixedCurrency = currencies.length > 1;
  const currencyUnsafe = totalRows.some((row) => hasPopulatedTotal(row) && !normalizedCurrency(row.currency));
  const totalsUnavailable = mixedCurrency || currencyUnsafe;
  const subtotal = totalsUnavailable ? { budget: null, committed: null, forecast: null, invoiced: null } : {
    budget: sumNullable(costRows, "budget"),
    committed: sumNullable(costRows, "committed"),
    forecast: sumNullable(costRows, "forecast"),
    invoiced: sumNullable(costRows, "invoiced"),
  };
  const contingency = rows.find((row) => row.category === "contingency");
  const other = rows.find((row) => row.category === "other");
  const grandTotal = totalsUnavailable ? { budget: null, committed: null, forecast: null, invoiced: null } : {
    budget: subtotal.budget == null ? null : subtotal.budget + (Number(contingency?.budget) || 0) + (Number(other?.budget) || 0),
    committed: subtotal.committed == null ? null : subtotal.committed + (Number(contingency?.committed) || 0) + (Number(other?.committed) || 0),
    forecast: subtotal.forecast == null ? null : subtotal.forecast + (Number(contingency?.forecast) || 0) + (Number(other?.forecast) || 0),
    invoiced: subtotal.invoiced == null ? null : subtotal.invoiced + (Number(contingency?.invoiced) || 0) + (Number(other?.invoiced) || 0),
  };
  for (const total of [subtotal, grandTotal]) {
    total.variance = total.budget != null && total.forecast != null
      ? total.budget - total.forecast
      : null;
  }
  return { subtotal, grandTotal, currency: currencies.length === 1 ? currencies[0] : null, mixedCurrency, currencyUnsafe };
}

function varianceColor(value) {
  return value == null ? T.light : value < 0 ? T.red : value > 0 ? T.green : T.mid;
}

function Variance({ value, currency }) {
  return <span style={{ color: varianceColor(value), fontWeight: T.fwSemibold }}>{formatBudgetCell(value, currency)}</span>;
}

function BudgetValue({ row, field }) {
  return formatBudgetCell(row[field], row.currency);
}

function budgetPayload(draft) {
  return {
    category: draft.category,
    budget: numberOrNull(draft.budget),
    committed: numberOrNull(draft.committed),
    forecast: numberOrNull(draft.forecast),
    invoiced: numberOrNull(draft.invoiced),
    costRsf: numberOrNull(draft.costRsf),
    currency: draft.currency || null,
    note: draft.note?.trim() || null,
  };
}

function emptyDraft(category = "other", currency = "USD") {
  return { category, budget: "", committed: "", forecast: "", invoiced: "", costRsf: "", currency, note: "" };
}

function BudgetEditor({ draft, setDraft, onSave, onCancel, saving, error, categoryOptions }) {
  const set = (key) => (event) => setDraft((current) => ({ ...current, [key]: event.target.value }));
  return (
    <div data-testid="project-cat-budget-editor" style={{ background: T.blueSoft, borderRadius: 7, padding: 10, marginTop: 8 }}>
      <div style={{ display: "grid", gridTemplateColumns: "1.1fr repeat(5, minmax(78px, 1fr))", gap: 6, alignItems: "end" }}>
        <label style={{ color: T.mid, fontSize: T.fsXs }}>Category
          <select value={draft.category} onChange={set("category")} style={inputStyle}>
            {categoryOptions.map((key) => <option value={key} key={key}>{BUDGET_CATEGORY_LABELS[key]}</option>)}
          </select>
        </label>
        {[
          ["budget", "Budget"],
          ["committed", "Committed"],
          ["forecast", "Forecast"],
          ["invoiced", "Invoiced"],
          ["costRsf", "Cost / RSF"],
        ].map(([key, label]) => (
          <label key={key} style={{ color: T.mid, fontSize: T.fsXs }}>{label}
            <input type="number" step="0.01" value={draft[key]} onChange={set(key)} style={inputStyle} />
          </label>
        ))}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "120px 1fr", gap: 6, marginTop: 7 }}>
        <label style={{ color: T.mid, fontSize: T.fsXs }}>Currency
          <input value={draft.currency} onChange={set("currency")} maxLength={3} style={inputStyle} />
        </label>
        <label style={{ color: T.mid, fontSize: T.fsXs }}>Note
          <input value={draft.note} onChange={set("note")} style={inputStyle} />
        </label>
      </div>
      {error && <div role="alert" style={{ color: T.red, fontSize: T.fsXs, marginTop: 6 }}>{error}</div>}
      <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
        <button type="button" data-testid="project-cat-budget-save-btn" onClick={onSave} disabled={saving} style={{ border: `1px solid ${T.blue}`, borderRadius: 5, background: T.blue, color: T.white, padding: "5px 9px", fontSize: T.fsXs }}>{saving ? "Saving…" : "Save"}</button>
        <button type="button" data-testid="project-cat-budget-cancel-btn" onClick={onCancel} disabled={saving} style={{ border: `1px solid ${T.border}`, borderRadius: 5, background: T.white, color: T.mid, padding: "5px 9px", fontSize: T.fsXs }}>Cancel</button>
      </div>
    </div>
  );
}

function BudgetTable({ budgets, canEdit, editingId, saving = false, onEdit, onDelete }) {
  const { subtotal, grandTotal, currency, mixedCurrency, currencyUnsafe } = deriveBudgetTotals(budgets);
  const rows = budgets.filter((row) => row.category !== "tia").sort((a, b) => CATEGORY_ORDER.indexOf(a.category) - CATEGORY_ORDER.indexOf(b.category));
  const renderAmount = (row, key) => <BudgetValue row={row} field={key} />;
  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 650 }}>
        <thead>
          <tr>
            {["Category", "Budget", "Committed", "Forecast", "Variance", "Invoiced"].map((label) => <th key={label} style={{ textAlign: label === "Category" ? "left" : "right", padding: "6px 7px", borderBottom: `2px solid ${T.border}`, color: T.mid, fontSize: T.fsXs, fontWeight: T.fwBold }}>{label}</th>)}
            {canEdit && <th style={{ borderBottom: `2px solid ${T.border}` }} />}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id} data-testid={`project-cat-budget-row-${row.id}`} style={{ borderBottom: `1px solid ${T.border}` }}>
              <td style={{ padding: "7px", fontSize: T.fsSm, color: row.category === "tia" ? T.mid : T.dark, fontStyle: row.category === "tia" ? "italic" : "normal" }}>
                <div>{BUDGET_CATEGORY_LABELS[row.category] || row.category}</div>
                {(row.costRsf != null || row.note) && <div style={{ color: T.light, fontSize: T.fsXxs, fontStyle: "normal", marginTop: 2 }}>
                  {row.costRsf != null && `Cost / RSF ${formatBudgetCell(row.costRsf, "")}`}
                  {row.costRsf != null && row.note && " · "}
                  {row.note || ""}
                </div>}
              </td>
              <td style={{ padding: "7px", textAlign: "right", fontSize: T.fsSm }}>{renderAmount(row, "budget")}</td>
              <td style={{ padding: "7px", textAlign: "right", fontSize: T.fsSm }}>{renderAmount(row, "committed")}</td>
              <td style={{ padding: "7px", textAlign: "right", fontSize: T.fsSm }}>{renderAmount(row, "forecast")}</td>
              <td style={{ padding: "7px", textAlign: "right", fontSize: T.fsSm }}><Variance value={row.variance ?? (row.budget != null && row.forecast != null ? Number(row.budget) - Number(row.forecast) : null)} currency={row.currency} /></td>
              <td style={{ padding: "7px", textAlign: "right", fontSize: T.fsSm }}>{renderAmount(row, "invoiced")}</td>
              {canEdit && <td style={{ padding: "7px", whiteSpace: "nowrap", textAlign: "right" }}>
                <button type="button" onClick={() => onEdit(row)} disabled={saving} style={{ border: 0, background: "none", color: T.blue, cursor: "pointer", fontSize: T.fsXs }}>Edit</button>
                <button type="button" onClick={() => onDelete(row)} disabled={saving || editingId === row.id} style={{ border: 0, background: "none", color: T.red, cursor: "pointer", fontSize: T.fsXs, marginLeft: 4 }}>Remove</button>
              </td>}
            </tr>
          ))}
          <TotalRow testId="project-cat-budget-subtotal" label="Subtotal" values={subtotal} currency={currency} />
          <TotalRow testId="project-cat-budget-grand-total" label="Grand Total" values={grandTotal} currency={currency} strong />
          {budgets.filter((row) => row.category === "tia").map((row) => (
            <tr key={`tia-${row.id}`} data-testid="project-cat-budget-tia-row" style={{ borderTop: `1px solid ${T.border}` }}>
              <td style={{ padding: "7px", color: T.mid, fontStyle: "italic", fontSize: T.fsSm }}>TIA (separate allowance)</td>
              <td style={{ padding: "7px", textAlign: "right", fontSize: T.fsSm }}>{renderAmount(row, "budget")}</td>
              <td colSpan={4} style={{ padding: "7px", color: T.light, fontSize: T.fsXs }}>{row.note || "Not included in totals"}</td>
              {canEdit && <td style={{ padding: "7px", whiteSpace: "nowrap", textAlign: "right" }}>
                <button type="button" onClick={() => onEdit(row)} disabled={saving} style={{ border: 0, background: "none", color: T.blue, cursor: "pointer", fontSize: T.fsXs }}>Edit</button>
                <button type="button" onClick={() => onDelete(row)} disabled={saving || editingId === row.id} style={{ border: 0, background: "none", color: T.red, cursor: "pointer", fontSize: T.fsXs, marginLeft: 4 }}>Remove</button>
              </td>}
            </tr>
          ))}
        </tbody>
      </table>
      {(mixedCurrency || currencyUnsafe) && <div data-testid="project-cat-budget-currency-warning" style={{ color: T.amberDark, fontSize: T.fsXs, marginTop: 7 }}>Totals are unavailable because active category rows use {mixedCurrency ? "multiple currencies" : "an unspecified currency"}. Rows retain their source currencies.</div>}
      {rows.some((row) => row.note) && <div style={{ color: T.mid, fontSize: T.fsXs, marginTop: 7 }}>TIA remains a separate allowance and is excluded from Grand Total; Other is included when present.</div>}
    </div>
  );
}

function TotalRow({ testId, label, values, currency, strong }) {
  return (
    <tr data-testid={testId} style={{ borderTop: `1px solid ${T.border}`, background: strong ? T.blueSoft : T.white }}>
      <td style={{ padding: "8px 7px", fontSize: T.fsSm, fontWeight: strong ? T.fwBold : T.fwSemibold, color: T.dark }}>{label}</td>
      {["budget", "committed", "forecast"].map((key) => <td key={key} style={{ padding: "8px 7px", textAlign: "right", fontSize: T.fsSm, fontWeight: strong ? T.fwBold : T.fwSemibold }}>{formatBudgetCell(values[key], currency)}</td>)}
      <td style={{ padding: "8px 7px", textAlign: "right", fontSize: T.fsSm }}><Variance value={values.variance} currency={currency} /></td>
      <td style={{ padding: "8px 7px", textAlign: "right", fontSize: T.fsSm, fontWeight: strong ? T.fwBold : T.fwSemibold }}>{formatBudgetCell(values.invoiced, currency)}</td>
    </tr>
  );
}

export function ProjectBudgetSection({ projectId, project, budgets, canEdit, onChange, compact = false, onEditorState }) {
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  // Optional, backward-compatible editor-state reporter ({ editing, busy }).
  const reportRef = useRef(onEditorState);
  reportRef.current = onEditorState;
  const savingRef = useRef(false);
  const mountedRef = useRef(true);
  useEffect(() => { reportRef.current?.({ editing: Boolean(editingId), busy: saving }); }, [editingId, saving]);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      reportRef.current?.({ editing: false, busy: savingRef.current });
    };
  }, []);
  const { grandTotal, currency: totalCurrency, mixedCurrency, currencyUnsafe } = useMemo(() => deriveBudgetTotals(budgets), [budgets]);
  const availableCategories = CATEGORY_ORDER.filter((key) => !budgets.some((row) => row.category === key));
  const projectCurrency = normalizedCurrency(project.currency);
  const categoryCurrencyMismatch = grandTotal.budget != null && (!totalCurrency || !projectCurrency || totalCurrency !== projectCurrency);
  const mismatch = !mixedCurrency && !currencyUnsafe && !categoryCurrencyMismatch && project.approvedBudget != null && grandTotal.budget != null && Number(project.approvedBudget) !== Number(grandTotal.budget);

  const startAdd = () => {
    if (!canEdit || savingRef.current) return;
    setError(null);
    setEditingId("new");
    setDraft(emptyDraft(availableCategories[0] || "other", project.currency || "USD"));
  };
  const startEdit = (row) => {
    if (!canEdit || savingRef.current) return;
    setError(null);
    setEditingId(row.id);
    setDraft({ ...row, budget: row.budget ?? "", committed: row.committed ?? "", forecast: row.forecast ?? "", invoiced: row.invoiced ?? "", costRsf: row.costRsf ?? "", currency: row.currency || project.currency || "USD", note: row.note || "" });
  };
  const closeEditor = () => { setEditingId(null); setDraft(null); setError(null); };
  const cancelEditor = () => { if (!savingRef.current) closeEditor(); };
  const save = async () => {
    if (!canEdit || !draft?.category || savingRef.current) return;
    savingRef.current = true; setSaving(true); setError(null);
    try {
      const result = editingId === "new"
        ? await createProjectBudget(projectId, budgetPayload(draft))
        : await updateProjectBudget(projectId, editingId, budgetPayload(draft));
      onChange((previous) => editingId === "new" ? [...previous, result] : previous.map((row) => row.id === result.id ? result : row));
      closeEditor();
    } catch (err) {
      setError(err.message || "Could not save budget row");
      log.warn("ProjectBudgetSection: save failed", { projectId, err: String(err) });
    } finally {
      savingRef.current = false; setSaving(false);
      if (!mountedRef.current) reportRef.current?.({ editing: false, busy: false });
    }
  };
  const remove = async (row) => {
    if (!canEdit || savingRef.current) return;
    if (!window.confirm(`Remove ${BUDGET_CATEGORY_LABELS[row.category] || row.category} budget row?`)) return;
    savingRef.current = true; setSaving(true); setError(null);
    try {
      await deleteProjectBudget(projectId, row.id);
      onChange((previous) => previous.filter((item) => item.id !== row.id));
    } catch (err) {
      setError(err.message || "Could not remove budget row");
      log.warn("ProjectBudgetSection: delete failed", { projectId, err: String(err) });
    } finally {
      savingRef.current = false; setSaving(false);
      if (!mountedRef.current) reportRef.current?.({ editing: false, busy: false });
    }
  };

  return (
    <div data-testid="project-category-budget-panel" style={{ marginTop: compact ? 12 : 0 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        <div>
          <h3 style={{ margin: 0, color: T.mid, fontSize: T.fsXs, fontWeight: T.fwBold, letterSpacing: 0.5, textTransform: "uppercase" }}>Category Budget</h3>
          <div style={{ color: T.light, fontSize: T.fsXs, marginTop: 2 }}>Budget values retain their source currency per row.</div>
        </div>
        {canEdit && availableCategories.length > 0 && <button type="button" data-testid="project-cat-budget-edit-btn" onClick={startAdd} disabled={saving} style={{ border: `1px solid ${T.blue}`, borderRadius: 5, background: T.white, color: T.blue, padding: "4px 8px", fontSize: T.fsXs }}>+ Add</button>}
      </div>
      {budgets.length === 0 && !draft ? <div style={{ color: T.light, fontSize: T.fsSm, fontStyle: "italic", padding: "8px 0" }}>No category budget rows recorded.</div> : <BudgetTable budgets={budgets} canEdit={canEdit} editingId={editingId} saving={saving} onEdit={startEdit} onDelete={remove} />}
      {canEdit && editingId && draft && (
        <BudgetEditor
          draft={draft}
          setDraft={setDraft}
          onSave={save}
          onCancel={cancelEditor}
          saving={saving}
          error={error}
          categoryOptions={editingId === "new" ? availableCategories : CATEGORY_ORDER}
        />
      )}
      {categoryCurrencyMismatch && <div data-testid="project-cat-budget-reconciliation-warning" style={{ marginTop: 8, padding: "6px 9px", borderRadius: 5, background: T.utilAmber, color: T.amberDark, fontSize: T.fsXs }}>Category Grand Total cannot be reconciled to the approved project budget because the currencies do not match ({totalCurrency || "unspecified"} vs {projectCurrency || "unspecified"}).</div>}
      {mismatch && <div data-testid="project-cat-budget-mismatch-warning" style={{ marginTop: 8, padding: "6px 9px", borderRadius: 5, background: T.utilAmber, color: T.amberDark, fontSize: T.fsXs }}>Category Grand Total {formatBudgetCell(grandTotal.budget, totalCurrency)} differs from approved project budget {formatBudgetCell(project.approvedBudget, project.currency)}.</div>}
      {error && !editingId && <div role="alert" style={{ color: T.red, fontSize: T.fsXs, marginTop: 6 }}>{error}</div>}
    </div>
  );
}